/**
 * A person's own order for a form's elements — the USER rung of #1466.
 *
 * ── Where it lives, and the RAISE that decided it (M4, stamped) ─────────────
 * `core.user_setting`, key `form.order`. NOT a `scope='user'` row in
 * `core.form`, which is where a "personal form layout" naturally wants to go.
 *
 * `core.form` carries a partial unique index for the SYSTEM default and for the
 * PERSONA default (`core/0030:101-102`) and none at all for
 * `scope='user' and is_default`. `core.resolve_form` is `language sql` and reads
 * that layer as a scalar subquery inside `coalesce(...)`
 * (`2026082121:219-222`), so a second personal default is not a tie-break the
 * resolver loses — it is `more than one row returned by a subquery used as an
 * expression`, raised, and the record surface for that user on that entity stops
 * resolving a form at all. One duplicate row, for a preference about where a box
 * sits.
 *
 * `core.user_setting` cannot reach that state: its primary key IS
 * `(user_id, key)` (`0035:15`). It needs no migration, it is already the proven
 * per-user round trip (`my_settings()` / `set_user_setting()`, the same pair
 * `list/useColumnWidths.ts` and `record/useSlotFrequency.ts` use), and it keeps
 * the ALM boundary for free — a solution exports `core.form`, and one person's
 * layout is not something a solution should carry between environments.
 *
 * ── What a person may move, and what stays out of reach ─────────────────────
 * SECTIONS, and the FIELDS within a section. That is the whole of it, and the
 * limit is deliberate rather than unimplemented: `required`, `readOnly`,
 * `classification`, `core.field_acl` and the entity's write door decide what a
 * field MEANS and are governed elsewhere. `applyFormOrder` in
 * `@solum/experience` is a permutation of the built form and has no path to any
 * of them — see its header and `formOrder.test.ts` for the property.
 *
 *   shape  `{ "<entity>": { sections: [title…], fields: { "<title>": [key…] } } }`
 *
 * Keyed by ENTITY, not by form id: the same entity's form resolved through a
 * different layer is still the same set of sections to this person, and having
 * their order evaporate because an admin published a tenant default would read
 * as the preference being lost.
 *
 * ── Writes are DEBOUNCED, for the same reason widths are ────────────────────
 * A reorder gesture emits a move per crossing, not per gesture. The local state
 * leads and the server sees where the hand stopped.
 *
 * ── The write half is BUILT and UNREACHED, and that is a decision ───────────
 * `setSections`, `setFields`, `reset` and `moved` have no non-test caller.
 * `RecordBody` reads `.order` and nothing else. There is no editor, because a
 * reorder GESTURE cannot be verified in this package's suite — the console
 * tests have no DOM — and a gesture shipped without a test that drives it is
 * the kind of half-shipped face this repo keeps paying for.
 *
 * So why keep it rather than cut it, when an export whose only caller is a test
 * certifies a path nothing walks? Because of what these particular functions
 * guard. `set_user_setting` replaces the value WHOLE: it is an upsert of one
 * jsonb blob, so a write that forgets to carry the rest of the bag silently
 * deletes every OTHER entity's layout for that person. One line, invisible in
 * review, permanent for whoever it happens to. `withOrder` and `withoutOrder`
 * exist to make that impossible, and `useFormOrder.test.ts` proves each of them
 * by mutation. That guard has to already be right on the day the first write
 * appears — the same argument the reader makes for itself two paragraphs up,
 * and it does not become weaker because the writer is the one making it.
 *
 * The honest statement, then, is not "there is no write half". It is: the write
 * half is here, it is guarded, nothing reaches it, and the slice that adds the
 * editor adds a gesture test — not a document rewrite.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  formOrderFor,
  moved as movedShared,
  withFormOrder,
  withoutFormOrder,
  type FormOrder,
  type FormOrderMap,
  type FormOrderBag,
} from '@solum/experience'
import { useSolum } from '../data/client'
import { usePersona } from '../app/persona'

/** WHERE one personal form order lives. Both dimensions, together, because a
 *  write that carries one of them is a write that can land on the wrong key. */
export interface FormOrderAddress { entity: string; persona: string | null }


export const FORM_ORDER_KEY = 'form.order'
const SAVE_DEBOUNCE_MS = 600

/** every entity this person has ordered */
export type { FormOrderMap, FormOrderBag }

/**
 * This person's order for one entity, or null when they have never set one.
 *
 * SCOPED BY PERSONA since #2009, with the legacy entity-only entry as the
 * fallback for a persona that has not authored its own. The same person acting
 * as Sales and as Finance was previously handed one order for both, on forms
 * those personas legitimately lay out differently.
 */
export function orderFor(
  settings: Record<string, unknown> | undefined, entity: string, persona?: string | null,
): FormOrder | null {
  return formOrderFor(settings, FORM_ORDER_KEY, entity, persona)
}

/** The bag with one entity's order replaced, for this persona only. */
export function withOrder(
  all: FormOrderBag, entity: string, next: FormOrder, persona?: string | null,
): FormOrderBag {
  return withFormOrder(all, entity, next, persona)
}

/**
 * The bag with one entity's order REMOVED — the key deleted, not set to `{}`.
 *
 * "Reset to the definition" has to leave nothing behind, otherwise the next
 * read has to decide whether an empty object means "no preference" or "a
 * preference that happens to be empty", and the document grows a dead key for
 * every entity anybody ever visited.
 */
export function withoutOrder(
  all: FormOrderBag, entity: string, persona?: string | null,
): FormOrderBag {
  return withoutFormOrder(all, entity, persona)
}

/** Move one item of a list, by index — the primitive a drag or a keyboard
 *  reorder both reduce to. Out-of-range indices leave the list alone. */
export function moved<T>(items: T[], from: number, to: number): T[] {
  return movedShared(items, from, to)
}

export interface FormOrderHandle {
  /** the stored order, or null when this person has never set one */
  order: FormOrder | null
  /** true once the settings read has settled — a REFUSED read is settled too */
  ready: boolean
  /** true when there is something to reset */
  pinned: boolean
  saving: boolean
  /** Explicit save used by the in-place editor. It resolves only after the
   * server accepted the whole preference bag. `null` means inherit/reset. */
  save: (order: FormOrder | null) => Promise<void>
  setSections: (titles: string[]) => void
  setFields: (section: string, keys: string[]) => void
  reset: () => void
}

export function useFormOrder(entity: string): FormOrderHandle {
  const { client, isLive } = useSolum()
  const qc = useQueryClient()

  const q = useQuery({
    // The SAME key `data/userFormat.ts`, `useColumnWidths` and
    // `useSlotFrequency` use: `my_settings()` returns the whole bag in one
    // call, so a second key would be a second round trip for cached data.
    queryKey: ['user-settings'],
    queryFn: () => client.raw().rpcScalar<Record<string, unknown>>('my_settings'),
    enabled: isLive,
    staleTime: 60_000,
  })

  /* A refused settings read is SETTLED, not pending (the distinction
     `useColumnWidths` draws): there is no stored order, the form renders in the
     definition's order, and that is a degraded presentation rather than a false
     statement about a record. Without this, `isLoading` alone would leave the
     error state deciding a render nothing argued for. */
  /* #2009 — the ACTING persona scopes the read. It is the session's persona
     rather than an authoring target: this is the person's own arrangement of
     the form they are looking at, so it follows what they are acting as. A tab
     that has chosen none reads the legacy entity-only entry, which is also the
     fallback for any persona that has not authored its own. */
  const persona = usePersona()
  const stored = useMemo(() => orderFor(q.data, entity, persona || null), [q.data, entity, persona])

  /* Local leads, server follows — a refetch landing mid-gesture must not snap
     the section the hand is still moving back to where the server last saw it.
     A BOX, not a bare value: "nothing local yet" and "locally reset to the
     definition" are two different states, and a bare `null` would collapse them
     so that Reset showed the stored order again until the write came back. */
  const [local, setLocal] = useState<{ value: FormOrder | null } | null>(null)

  /* WHERE a personal order lives: entity AND persona, together.
   *
   * They were handled separately — entity cleared the local override, persona
   * cleared the override and the pending timer — and the pair drifted exactly
   * as two copies of one rule do. A reorder on one record followed by
   * navigation to another inside the 600ms window left the timer running; the
   * mutation then read `entity` out of the CURRENT closure and wrote the first
   * record's arrangement onto the second record's key. The persona axis had
   * been fixed and the entity axis had not, which is the same defect with one
   * dimension renamed.
   *
   * One address, one reset, one payload. `liveAddress` is assigned during
   * render rather than in an effect because it is read by an async completion
   * that can resolve before an effect has run. */
  const address = useMemo(
    () => ({ entity, persona: persona || null }), [entity, persona])
  const addressKey = `${address.entity}\u0000${address.persona ?? ''}`
  const liveAddress = useRef(addressKey)
  liveAddress.current = addressKey

  /* #2009 — the write carries the persona it was MADE under.
   *
   * The mutation used to read `persona` from scope, and the debounce below
   * fires 600ms later. Reorder while acting as Sales, switch to Finance inside
   * that window, and the queued write landed on FINANCE's key carrying SALES's
   * order — silently replacing an arrangement the person never touched. The
   * persona dimension is exactly what made that reachable, so it travels with
   * the payload rather than being looked up at fire time. */
  const saveMutation = useMutation({
    mutationFn: ({ next, target }: { next: FormOrder | null; target: FormOrderAddress }) => {
      if (!q.isSuccess) throw new Error('The existing form preference has not been read yet')
      const latest = qc.getQueryData<Record<string, unknown>>(['user-settings']) ?? q.data
      const all = (latest?.[FORM_ORDER_KEY] ?? {}) as FormOrderBag
      return client.raw().rpcScalar('set_user_setting', {
        p_key: FORM_ORDER_KEY,
        /* Writes and resets land on the SAME scope the read came from, so a
           reset cannot remove an entry the person was not looking at. With no
           acting persona both fall through to the legacy entry, exactly as
           before this change. */
        /* Both dimensions come from the PAYLOAD. Reading either from scope is
           reading it at fire time, which is the whole defect. */
        p_value: next
          ? withOrder(all, target.entity, next, target.persona)
          : withoutOrder(all, target.entity, target.persona),
      })
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['user-settings'] }) },
  })

  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const queueSave = useCallback((next: FormOrder | null) => {
    if (!isLive) return
    if (timer.current) clearTimeout(timer.current)
    // The address is captured HERE, with the gesture — not read when the timer fires.
    const target = address
    timer.current = setTimeout(() => saveMutation.mutate({ next, target }), SAVE_DEBOUNCE_MS)
  }, [isLive, saveMutation, address])
  // A gesture still settling when the user navigates away would otherwise be
  // lost with the timer.
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current) }, [])

  /* A pending gesture is DROPPED when the address changes under it, and the
     local override with it. Carrying either across would show — and then
     persist — one address's arrangement under another's name. ONE effect for
     both dimensions: the earlier pair cleared different things for entity and
     for persona, and the difference was the bug. */
  useEffect(() => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null }
    setLocal(null)
  }, [addressKey])

  const order = local ? local.value : stored

  const write = useCallback((next: FormOrder) => {
    setLocal({ value: next })
    queueSave(next)
  }, [queueSave])

  return {
    order,
    ready: q.isSuccess,
    pinned: Boolean(order),
    saving: saveMutation.isPending,
    save: async (next) => {
      if (timer.current) { clearTimeout(timer.current); timer.current = null }
      // An explicit Save/Reset is immediate, so "the address it was made at"
      // and "the address now" are the same — but it is passed rather than
      // looked up, so there is ONE way a write learns where it goes.
      const at = addressKey
      if (isLive) await saveMutation.mutateAsync({ next, target: address })
      /* The await is a window, and the address can move across it. Applying the
         override anyway would put one record's order on screen under another
         record's name — and the next gesture would persist it there. The write
         itself already landed correctly; what must not follow it is the local
         display of a result that belongs somewhere else. */
      if (liveAddress.current !== at) return
      setLocal({ value: next })
    },
    setSections: (titles) => write({ ...(order ?? {}), sections: titles }),
    setFields: (section, keys) => write({
      ...(order ?? {}),
      fields: { ...(order?.fields ?? {}), [section]: keys },
    }),
    reset: () => { setLocal({ value: null }); queueSave(null) },
  }
}
